import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step10/char.svg';
import { useEmploymentReadinessContext } from '../../hooks/use-form-context.hooks';
import {
  AgreementLevel,
  dispatchUpdateEmploymentReadiness,
} from '../../redux/slice/form-employment-readiness';
import { dispatchUpsertPreEmploymentAssessment } from '../../redux/slice/pre-employment-assessment';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

import { JobSkillOptions } from './JobInterview';

interface FormData {
  attitudeTakeResponsibility: string;
  attitudeGainsKnowledge: string;
  attitudeWorkLongHours: string;
  attitudeSatisfactionFromWork: string;
  attitudeFollowSchedule: string;
  attitudePunctual: string;
  attitudeCompleteTasksOnTime: string;
  attitudePoliteAtWork: string;
  attitudeExpressesOpinionsClearly: string;
  attitudeCommunicatesWithManager: string;
  attitudeResolvesConflict: string;
  attitudeAppropriateLanguage: string;
  attitudeRespondsAppropriately: string;
  attitudeInitiateCompleteTasks: string;
  attitudeIntegrity: string;
}

interface ComponentProps {
  step: number;
  onBack: () => void;
}

export const WorkAttitude: React.FC<ComponentProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    attitudeTakeResponsibility,
    attitudeGainsKnowledge,
    attitudeWorkLongHours,
    attitudeSatisfactionFromWork,
    attitudeFollowSchedule,
    attitudePunctual,
    attitudeCompleteTasksOnTime,
    attitudePoliteAtWork,
    attitudeExpressesOpinionsClearly,
    attitudeCommunicatesWithManager,
    attitudeResolvesConflict,
    attitudeAppropriateLanguage,
    attitudeRespondsAppropriately,
    attitudeInitiateCompleteTasks,
    attitudeIntegrity,
  } = useEmploymentReadinessContext();
  const [selectedAttitudeTakeResponsibility, setSelectedAttitudeTakeResponsibility] = useState(
    attitudeTakeResponsibility || '',
  );
  const [selectedGainsKnowledge, setSelectedGainsKnowledge] = useState(
    attitudeGainsKnowledge || '',
  );
  const [selectedWorkLongHours, setSelectedWorkLongHours] = useState(attitudeWorkLongHours || '');
  const [selectedSatisfactionFromWork, setSelectedSatisfactionFromWork] = useState(
    attitudeSatisfactionFromWork || '',
  );
  const [selectedFollowSchedule, setSelectedFollowSchedule] = useState(
    attitudeFollowSchedule || '',
  );
  const [selectedPunctual, setSelectedPunctual] = useState(attitudePunctual || '');
  const [selectedCompleteTasksOnTime, setSelectedCompleteTasksOnTime] = useState(
    attitudeCompleteTasksOnTime || '',
  );
  const [selectedPoliteAtWork, setSelectedPoliteAtWork] = useState(attitudePoliteAtWork || '');
  const [selectedExpressesOpinionsClearly, setSelectedExpressesOpinionsClearly] = useState(
    attitudeExpressesOpinionsClearly || '',
  );

  const [selectedCommunicatesWithManager, setSelectedCommunicatesWithManager] = useState(
    attitudeCommunicatesWithManager || '',
  );
  const [selectedResolvesConflict, setSelectedResolvesConflict] = useState(
    attitudeResolvesConflict || '',
  );
  const [selectedAppropriateLanguage, setSelectedAppropriateLanguage] = useState(
    attitudeAppropriateLanguage || '',
  );
  const [selectedRespondsAppropriately, setSelectedRespondsAppropriately] = useState(
    attitudeRespondsAppropriately || '',
  );
  const [selectedInitiateCompleteTasks, setSelectedInitiateCompleteTasks] = useState(
    attitudeInitiateCompleteTasks || '',
  );
  const [selectedIntegrity, setSelectedIntegrity] = useState(attitudeIntegrity || '');

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    switch (field as keyof FormData) {
      case 'attitudeTakeResponsibility':
        setSelectedAttitudeTakeResponsibility(event.target.value);
        setError({ ...error, attitudeTakeResponsibility: '' });
        break;
      case 'attitudeGainsKnowledge':
        setSelectedGainsKnowledge(event.target.value);
        setError({ ...error, attitudeGainsKnowledge: '' });
        break;
      case 'attitudeWorkLongHours':
        setSelectedWorkLongHours(event.target.value);
        setError({ ...error, attitudeWorkLongHours: '' });
        break;
      case 'attitudeSatisfactionFromWork':
        setSelectedSatisfactionFromWork(event.target.value);
        setError({ ...error, attitudeSatisfactionFromWork: '' });
        break;
      case 'attitudeFollowSchedule':
        setSelectedFollowSchedule(event.target.value);
        setError({ ...error, attitudeFollowSchedule: '' });
        break;
      case 'attitudePunctual':
        setSelectedPunctual(event.target.value);
        setError({ ...error, attitudePunctual: '' });
        break;
      case 'attitudeCompleteTasksOnTime':
        setSelectedCompleteTasksOnTime(event.target.value);
        setError({ ...error, attitudeCompleteTasksOnTime: '' });
        break;
      case 'attitudePoliteAtWork':
        setSelectedPoliteAtWork(event.target.value);
        setError({ ...error, attitudePoliteAtWork: '' });
        break;
      case 'attitudeExpressesOpinionsClearly':
        setSelectedExpressesOpinionsClearly(event.target.value);
        setError({ ...error, attitudeExpressesOpinionsClearly: '' });
        break;
      case 'attitudeCommunicatesWithManager':
        setSelectedCommunicatesWithManager(event.target.value);
        setError({ ...error, attitudeCommunicatesWithManager: '' });
        break;
      case 'attitudeResolvesConflict':
        setSelectedResolvesConflict(event.target.value);
        setError({ ...error, attitudeResolvesConflict: '' });
        break;
      case 'attitudeAppropriateLanguage':
        setSelectedAppropriateLanguage(event.target.value);
        setError({ ...error, attitudeAppropriateLanguage: '' });
        break;
      case 'attitudeRespondsAppropriately':
        setSelectedRespondsAppropriately(event.target.value);
        setError({ ...error, attitudeRespondsAppropriately: '' });
        break;
      case 'attitudeInitiateCompleteTasks':
        setSelectedInitiateCompleteTasks(event.target.value);
        setError({ ...error, attitudeInitiateCompleteTasks: '' });
        break;
      case 'attitudeIntegrity':
        setSelectedIntegrity(event.target.value);
        setError({ ...error, attitudeIntegrity: '' });
        break;
    }
  };

  const [error, setError] = useState({
    attitudeTakeResponsibility: '',
    attitudeGainsKnowledge: '',
    attitudeWorkLongHours: '',
    attitudeSatisfactionFromWork: '',
    attitudeFollowSchedule: '',
    attitudePunctual: '',
    attitudeCompleteTasksOnTime: '',
    attitudePoliteAtWork: '',
    attitudeExpressesOpinionsClearly: '',

    attitudeCommunicatesWithManager: '',
    attitudeResolvesConflict: '',
    attitudeAppropriateLanguage: '',
    attitudeRespondsAppropriately: '',
    attitudeInitiateCompleteTasks: '',
    attitudeIntegrity: '',
  });
  const { handleSubmit } = useForm<FormData>();
  const backButtonClick = () => {
    onBack();
  };
  const submitAssessment: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted job attitude skills form.', {
      attitudeTakeResponsibility,
      attitudeGainsKnowledge,
      attitudeWorkLongHours,
      attitudeSatisfactionFromWork,
      attitudeFollowSchedule,
      attitudePunctual,
      attitudeCompleteTasksOnTime,
      attitudePoliteAtWork,
      attitudeExpressesOpinionsClearly,
      attitudeCommunicatesWithManager,
      attitudeResolvesConflict,
      attitudeAppropriateLanguage,
      attitudeRespondsAppropriately,
      attitudeInitiateCompleteTasks,
      attitudeIntegrity,
    });

    const error_message: string = 'Please select an option';
    if (!selectedAttitudeTakeResponsibility) {
      setError({ ...error, attitudeTakeResponsibility: error_message });
    } else if (!selectedWorkLongHours) {
      setError({ ...error, attitudeWorkLongHours: error_message });
    } else if (!selectedGainsKnowledge) {
      setError({ ...error, attitudeGainsKnowledge: error_message });
    } else if (!selectedSatisfactionFromWork) {
      setError({ ...error, attitudeSatisfactionFromWork: error_message });
    } else if (!selectedFollowSchedule) {
      setError({ ...error, attitudeFollowSchedule: error_message });
    } else if (!selectedPunctual) {
      setError({ ...error, attitudePunctual: error_message });
    } else if (!selectedCompleteTasksOnTime) {
      setError({ ...error, attitudeCompleteTasksOnTime: error_message });
    } else if (!selectedPoliteAtWork) {
      setError({ ...error, attitudePoliteAtWork: error_message });
    } else if (!selectedExpressesOpinionsClearly) {
      setError({ ...error, attitudeExpressesOpinionsClearly: error_message });
    } else {
      const dto: Record<keyof FormData, AgreementLevel> = {
        attitudeTakeResponsibility: selectedAttitudeTakeResponsibility as AgreementLevel,
        attitudeGainsKnowledge: selectedGainsKnowledge as AgreementLevel,
        attitudeWorkLongHours: selectedWorkLongHours as AgreementLevel,
        attitudeSatisfactionFromWork: selectedSatisfactionFromWork as AgreementLevel,
        attitudeFollowSchedule: selectedFollowSchedule as AgreementLevel,
        attitudePunctual: selectedPunctual as AgreementLevel,
        attitudeCompleteTasksOnTime: selectedCompleteTasksOnTime as AgreementLevel,
        attitudePoliteAtWork: selectedPoliteAtWork as AgreementLevel,
        attitudeExpressesOpinionsClearly: selectedExpressesOpinionsClearly as AgreementLevel,
        attitudeCommunicatesWithManager: selectedCommunicatesWithManager as AgreementLevel,
        attitudeResolvesConflict: selectedResolvesConflict as AgreementLevel,
        attitudeAppropriateLanguage: selectedAppropriateLanguage as AgreementLevel,
        attitudeRespondsAppropriately: selectedRespondsAppropriately as AgreementLevel,
        attitudeInitiateCompleteTasks: selectedInitiateCompleteTasks as AgreementLevel,
        attitudeIntegrity: selectedIntegrity as AgreementLevel,
      };
      await dispatch(dispatchUpdateEmploymentReadiness(dto));
      await dispatch(
        dispatchUpsertPreEmploymentAssessment({ didCompleteEmploymentReadiness: true }),
      );
      navigate('/pre-employment/skills/step1');
    }
  };

  useEffect(() => {
    if (attitudeTakeResponsibility) {
      setSelectedAttitudeTakeResponsibility(attitudeTakeResponsibility);
    }
    if (attitudeGainsKnowledge) {
      setSelectedGainsKnowledge(attitudeGainsKnowledge);
    }
    if (attitudeWorkLongHours) {
      setSelectedWorkLongHours(attitudeWorkLongHours);
    }
    if (attitudeSatisfactionFromWork) {
      setSelectedSatisfactionFromWork(attitudeSatisfactionFromWork);
    }
    if (attitudeFollowSchedule) {
      setSelectedFollowSchedule(attitudeFollowSchedule);
    }
    if (attitudePunctual) {
      setSelectedPunctual(attitudePunctual);
    }
    if (attitudeCompleteTasksOnTime) {
      setSelectedCompleteTasksOnTime(attitudeCompleteTasksOnTime);
    }
    if (attitudePoliteAtWork) {
      setSelectedPoliteAtWork(attitudePoliteAtWork);
    }
    if (attitudeExpressesOpinionsClearly) {
      setSelectedExpressesOpinionsClearly(attitudeExpressesOpinionsClearly);
    }
    if (attitudeCommunicatesWithManager) {
      setSelectedCommunicatesWithManager(attitudeCommunicatesWithManager);
    }
    if (attitudeResolvesConflict) {
      setSelectedResolvesConflict(attitudeResolvesConflict);
    }
    if (attitudeAppropriateLanguage) {
      setSelectedAppropriateLanguage(attitudeAppropriateLanguage);
    }
    if (attitudeRespondsAppropriately) {
      setSelectedRespondsAppropriately(attitudeRespondsAppropriately);
    }
    if (attitudeInitiateCompleteTasks) {
      setSelectedInitiateCompleteTasks(attitudeInitiateCompleteTasks);
    }
    if (attitudeIntegrity) {
      setSelectedIntegrity(attitudeIntegrity);
    }
  }, [
    attitudeTakeResponsibility,
    attitudeGainsKnowledge,
    attitudeWorkLongHours,
    attitudeSatisfactionFromWork,
    attitudeFollowSchedule,
    attitudePunctual,
    attitudeCompleteTasksOnTime,
    attitudePoliteAtWork,
    attitudeExpressesOpinionsClearly,
    attitudeCommunicatesWithManager,
    attitudeResolvesConflict,
    attitudeAppropriateLanguage,
    attitudeRespondsAppropriately,
    attitudeInitiateCompleteTasks,
    attitudeIntegrity,
  ]);

  return (
    <div>
      <p className="text-lg mt-7 md:px-10">
        Welcome to the "Toward Self Discovery" series, focusing on Work Attitude!
      </p>
      <p className="text-lg mt-7 md:px-10">
        Work attitude is the feeling you have towards the different aspects of the environment you
        have at work. How you feel about your work often affects your behaviour in the workplace.
      </p>
      <p className="text-lg mt-7 md:px-10">
        Your work attitude plays a significant role in how you approach and engage with your
        professional environment. It reflects your feelings and behaviors within the workplace,
        ultimately shaping your overall experience. In this section, we'll explore various aspects
        of your work attitude.
      </p>
      <p className="text-lg mt-7 md:px-10">
        You have the flexibility to complete this assessment in one sitting or return later to
        finish the remaining sections. Remember, this journey is all about self-discovery, and we're
        excited to be a part of it with you.
      </p>
      <p className="text-lg mt-7 md:px-10">
        We're here to help you understand your natural competencies, experiences, interests, and
        skill sets related to your work attitude. By reflecting on these statements and rating your
        agreement, you'll gain valuable insights into your work-related attitudes.
      </p>
      <p className="text-lg mt-7 md:px-10">
        Let's embark on this path toward self-improvement and career success together!
      </p>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">16</div>
        <div>
          <h4 className="text-2xl">
            Click here if you have never taken part in or
            <span className="font-semibold"> do not know how to perform in a job attitude.</span>
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 right-[-125px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className=" md:px-16"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitAssessment)}
      >
        <ol style={{ listStyle: 'upper-latin' }} type="A">
          <li className="mb-5 font-semibold">
            I take responsibility for my work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedAttitudeTakeResponsibility === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeTakeResponsibility')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeTakeResponsibility"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeTakeResponsibility && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeTakeResponsibility}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I gain knowledge and acquire skill from my work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedGainsKnowledge === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeGainsKnowledge')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeGainsKnowledge"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeGainsKnowledge && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeGainsKnowledge}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I do not feel tired even after working for a long period of time.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedWorkLongHours === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeWorkLongHours')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeWorkLongHours"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeWorkLongHours && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeWorkLongHours}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I gain satisfaction in my work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedSatisfactionFromWork === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeSatisfactionFromWork')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeSatisfactionFromWork"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeSatisfactionFromWork && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeSatisfactionFromWork}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I follow the assigned work schedule.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedFollowSchedule === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeFollowSchedule')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeFollowSchedule"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeFollowSchedule && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeFollowSchedule}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I arrive at my workplace punctually.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPunctual === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudePunctual')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudePunctual"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudePunctual && (
              <p className="text-red-500 font-light !text-sm">{error.attitudePunctual}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I complete my tasks on time.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedCompleteTasksOnTime === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeCompleteTasksOnTime')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeCompleteTasksOnTime"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeCompleteTasksOnTime && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeCompleteTasksOnTime}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I am polite and friendly at work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedPoliteAtWork === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudePoliteAtWork')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudePoliteAtWork"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudePoliteAtWork && (
              <p className="text-red-500 font-light !text-sm">{error.attitudePoliteAtWork}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I express my ideas and opinions clearly.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedExpressesOpinionsClearly === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'attitudeExpressesOpinionsClearly')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeExpressesOpinionsClearly"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeExpressesOpinionsClearly && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeExpressesOpinionsClearly}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I maintain good communication with my manager.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedCommunicatesWithManager === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'attitudeCommunicatesWithManager')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeCommunicatesWithManager"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeCommunicatesWithManager && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeCommunicatesWithManager}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I can resolve conflict and recognize different views at work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedResolvesConflict === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeResolvesConflict')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeResolvesConflict"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeResolvesConflict && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeResolvesConflict}</p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I use appropriate language in my workplace.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedAppropriateLanguage === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeAppropriateLanguage')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeAppropriateLanguage"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeAppropriateLanguage && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeAppropriateLanguage}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I respond appropriately to working instructions.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedRespondsAppropriately === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'attitudeRespondsAppropriately')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeRespondsAppropriately"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeRespondsAppropriately && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeRespondsAppropriately}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I initiative to complete tasks at work.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedInitiateCompleteTasks === option.value}
                      onChange={(event) =>
                        handleRadioChange(event, 'attitudeInitiateCompleteTasks')
                      }
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeInitiateCompleteTasks"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeInitiateCompleteTasks && (
              <p className="text-red-500 font-light !text-sm">
                {error.attitudeInitiateCompleteTasks}
              </p>
            )}
          </li>
          <li className="mb-5 font-semibold">
            I care about my integrity.
            <div className="rating-btns flex items-center mt-3">
              {JobSkillOptions.map((option) => (
                <div key={option.value} className="h-[55px] mr-4">
                  <label className="radio-container h-full block relative">
                    <input
                      checked={selectedIntegrity === option.value}
                      onChange={(event) => handleRadioChange(event, 'attitudeIntegrity')}
                      value={option.value}
                      className="w-full h-full absolute top-0 left-0 opacity-0"
                      type="radio"
                      name="attitudeIntegrity"
                    />
                    <span className="checkmark  !static !h-[55px] !w-auto !py-4 !px-5 !text-sm !whitespace-nowrap font-light !text-black">
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {error.attitudeIntegrity && (
              <p className="text-red-500 font-light !text-sm">{error.attitudeIntegrity}</p>
            )}
          </li>
        </ol>
        <div className="flex justify-between mt-20">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
